/**
 * Spanish Translations
 */

import type { Translations } from '../types';

export const es: Translations = {
  language: 'es',
  languageName: 'Espa\u00f1ol',

  common: {
    demo: 'DEMO',
    privacy: 'Privacidad',
    terms: 'Términos',
  },

  privacy: {
    title: 'Política de Privacidad',
    whatWeStore: 'Lo que almacenamos',
    whatWeStoreDesc:
      'Procesamos temporalmente la geometría facial para estimar la edad. No se almacenan imágenes ni plantillas biométricas después de la verificación.',
    whatWeDont: 'Lo que no almacenamos',
    whatWeDontDesc:
      'No almacenamos fotos, videos ni identificadores biométricos. Los datos faciales se procesan en el navegador y se descartan inmediatamente.',
    whyWeUseIt: 'Por qué lo usamos',
    whyWeUseItDesc:
      'La estimación de edad ayuda a los sitios web a cumplir con las regulaciones de restricción por edad sin requerir documentos personales.',
    readFull: 'Lee nuestra',
    privacyPolicyLink: 'Política de Privacidad',
  },

  theme: {
    system: 'Sistema',
    light: 'Claro',
    dark: 'Oscuro',
  },

  face: {
    noFace: 'No se detectó rostro',
    tooFar: 'Rostro muy lejos',
    tooClose: 'Rostro muy cerca',
    offCenter: 'Rostro descentrado',
    poorLighting: 'Luz insuficiente',
    good: 'Analizando, no te muevas...',
    holdStill: 'Analizando, no te muevas...',
    holdStillCountdown: 'No te muevas... {count}',
    processing: 'Procesando...',
    looking: 'No se detectó rostro',
    centerFace: 'Rostro descentrado',
    moveCloser: 'Rostro muy lejos',
    moveBack: 'Rostro muy cerca',
    keepInPosition: 'Mantén posición',
    perfect: 'Perfecto',
    centerInFrame: 'Coloca tu rostro en el encuadre',
    outsideOval: 'Rostro fuera del óvalo',
  },

  liveness: {
    blink: {
      title: 'Parpadea',
      hint: 'Parpadea lentamente una vez',
    },
    smile: {
      title: 'Sonr\u00ede',
      hint: 'Muestra una sonrisa natural',
    },
    lookLeft: {
      title: 'Gira a la izquierda',
      hint: 'Gira lentamente la cabeza hacia la izquierda',
    },
    lookRight: {
      title: 'Gira a la derecha',
      hint: 'Gira lentamente la cabeza hacia la derecha',
    },
    openMouth: {
      title: 'Abre la boca',
      hint: 'Abre bien la boca',
    },
    lookUp: {
      title: 'Mira hacia arriba',
      hint: 'Inclina la cabeza hacia arriba',
    },
    lookDown: {
      title: 'Mira hacia abajo',
      hint: 'Inclina la cabeza hacia abajo',
    },
    raiseBrows: {
      title: 'Levanta las cejas',
      hint: 'Levanta las cejas',
    },
    improveLighting: {
      title: 'Mejora la iluminaci\u00f3n',
      hint: 'Ve a un \u00e1rea m\u00e1s iluminada para continuar',
    },
  },

  document: {
    selectType: 'Selecciona el tipo de documento',
    captureFront: 'Captura el frente del documento',
    captureBack: 'Captura el reverso del documento',
    processing: 'Procesando tu documento...',
    tips: {
      goodLighting: 'Aseg\u00farate de tener buena iluminaci\u00f3n',
      flatSurface: 'Coloca el documento en una superficie plana',
      allCorners: 'Aseg\u00farate de que todas las esquinas sean visibles',
      noGlare: 'Evita reflejos y sombras',
    },
    types: {
      passport: 'Pasaporte',
      idCard: 'Documento de identidad',
      driversLicense: 'Licencia de conducir',
    },
  },

  ui: {
    continue: 'Continuar',
    next: 'Siguiente',
    retry: 'Intentar de nuevo',
    startOver: 'Empezar de nuevo',
    cancel: 'Cancelar',
    success: 'Control de Edad Aprobado',
    failure: 'Control de Edad Fallido',
    loading: 'Cargando...',
    error: 'Ocurrió un error',
    permissionRequired: 'Permiso requerido',
    cameraPermission: 'Se requiere acceso a la cámara',
    goBack: 'Volver',
    switchCamera: 'Cambiar cámara',
    completeVerification: 'Completar',
    continueToId: 'Continuar a Control de ID',
  },

  errors: {
    cameraNotFound: 'No se encontró cámara en este dispositivo',
    cameraPermissionDenied: 'El permiso de cámara fue denegado',
    cameraInUse: 'La cámara está siendo usada por otra aplicación',
    cameraNotSupported: 'La cámara no soporta la configuración requerida',
    cameraSecurityError: 'La cámara requiere conexión HTTPS',
    networkError: 'Error de red. Por favor, verifica tu conexión.',
    timeout: 'La solicitud expiró. Por favor, intenta de nuevo.',
    invalidApiKey: 'Clave de API inválida',
    sessionExpired: 'Sesión expirada. Por favor, comienza de nuevo.',
    verificationFailed: 'El control falló. Por favor, intenta de nuevo.',
    unknownError: 'Ocurrió un error inesperado',
    cameraAccessDeniedDetailed: 'Por favor, permite el acceso a la cámara para continuar.',
    cameraNotFoundDetailed:
      'No se detectó ninguna cámara. Por favor, conecta una cámara e intenta de nuevo.',
    cameraInUseDetailed:
      'La cámara está siendo usada por otra aplicación. Por favor, ciérrala e intenta de nuevo.',
    cameraOverconstrained:
      'La cámara no soporta la resolución solicitada. Intentando con configuración predeterminada...',
    networkTimeout:
      'La solicitud tardó demasiado. Por favor, verifica tu conexión e intenta de nuevo.',
    networkOffline: 'Parece que estás sin conexión. Por favor, verifica tu conexión a internet.',
    apiGenericError: 'Algo salió mal. Por favor, intenta de nuevo.',
    faceNotDetected: 'Por favor, posiciona tu rostro en el marco.',
    livenessFailed: 'La verificación de vida falló. Por favor, intenta de nuevo.',
    documentCaptureFailed:
      'No se pudo capturar el documento. Por favor, asegúrate de tener buena iluminación e intenta de nuevo.',
    initializationFailed: 'Error al cargar. Por favor, actualiza e intenta de nuevo.',
    modelLoadFailed:
      'Error al cargar los componentes necesarios. Por favor, actualiza e intenta de nuevo.',
    unexpectedError: 'Ocurrió un error inesperado. Por favor, intenta de nuevo.',
    configError: 'Error de configuración. Por favor, contacta al soporte.',
  },

  screens: {
    welcome: {
      title: 'Verificación de Edad 18+',
      subtitle: 'Verificación de Edad Anónima',
      startButton: 'Iniciar',
      secureNote: 'Seguro y Encriptado',
      ageTechBy: 'Tecnología de edad por AppBouncer',
      waitingCamera: 'Esperando Acceso a la Cámara',
      allowCamera: 'Permitir Acceso a la Cámara',
      noBiometric: 'No se almacenan datos biométricos',
      loading: 'Cargando...',
      loadingRequirements: 'Cargando requisitos...',
    },
    permissions: {
      title: 'Cargando ...',
      description: 'Por favor, permite el acceso a la cámara cuando se solicite',
      allowButton: 'Permitir Acceso a Cámara',
      privacyNote: 'Tus datos están encriptados y seguros',
      goodLighting: '¡Buena Iluminación!',
      goodLightingHint: 'Asegúrate de que tu rostro esté bien iluminado',
      getReady: 'Prepárate',
      removeCoverings: 'Quita los Cubrebocas',
      removeGlasses: 'Quita los Lentes',
    },
    facePosition: {
      title: 'Posiciona tu Rostro',
      hint: 'Centra tu rostro',
      step: 'Paso',
      stepOf: 'Paso {current} de {total}',
      lookingForFace: 'Buscando rostro...',
    },
    livenessCheck: {
      title: 'Verificación de Vida',
      followInstructions: 'Sigue las instrucciones',
      preparing: 'Preparando...',
      complete: '¡Vivacidad Verificada!',
      actionAutoDetected: 'La acción se detectará automáticamente',
      failed: 'Verificación de Vida Fallida',
      failedDescription:
        'No pudimos verificar tu vivacidad. Por favor, asegúrate de tener buena iluminación, mira directamente a la cámara y sigue las instrucciones en pantalla.',
      retryButton: 'Reintentar Control de Vivacidad',
      restartButton: 'Empezar de Nuevo',
    },
    ageEstimation: {
      title: 'No Te Muevas',
      analyzing: 'Analizando...',
      analyzingProgress: 'Analizando rostro {current} de {total}...',
      estimatedAge: 'Edad Estimada',
      holdStill: 'No Te Muevas',
      verified: 'Edad Verificada',
      verificationUncertain: 'Edad Incierta',
      estimationComplete: 'Control de edad completo',
      confirmedAge: 'Confirmado {minAge}+ años',
      couldNotConfirm: 'No se pudo confirmar edad {minAge}+',
      ageConfirmed: 'Edad Confirmada',
      aboveMinimumAge: 'Cumples con el requisito de edad mínima',
      belowMinimumAge: 'Menor de edad mínima',
      documentRequired: 'Documento Requerido',
      uploadDocumentMessage:
        'Por favor, sube un documento de identidad válido para verificar tu edad',
      idHint: 'DNI, pasaporte o carnet de conducir',
      confidenceLevels: 'Confianza por Umbral de Edad',
      confidence: 'confianza',
      years: 'años',
      continueToId: 'Continuar a Control de ID',
      demoComplete: 'Modo demo completado. En producción, serías redirigido.',
      improveLighting: 'Mejorar Iluminación',
      improveLightingHint: 'Ve a un área más iluminada para obtener resultados precisos',
    },
    documentSelect: {
      title: 'Seleccionar Tipo de Documento',
      description: 'Elige el tipo de documento de identidad que vas a escanear',
      idCard: 'Documento Nacional de Identidad',
      idCardHint: 'ID emitido por el gobierno',
      passport: 'Pasaporte',
      passportHint: 'Solo página de foto',
      driversLicense: 'Licencia de Conducir',
      driversLicenseHint: 'Licencia de conducir válida',
    },
    captureMode: {
      captureFrontSide: 'Capturar Lado Frontal',
      captureBackSide: 'Capturar Lado Posterior',
      chooseMethod: 'Elige cómo capturar tu documento',
      useCamera: 'Usar Cámara',
      useCameraHint: 'Toma una foto de tu documento',
      uploadFile: 'Subir Archivo',
      uploadFileHint: 'Seleccionar de tu dispositivo',
    },
    documentCamera: {
      frontSide: 'Lado Frontal',
      backSide: 'Lado Posterior',
      positionInFrame: 'Posiciona el documento en el marco',
      scanFront: 'Escanear Frente del Documento',
      scanBack: 'Escanear Reverso del Documento',
      keepFlat: 'Mantén el documento plano y bien iluminado',
      capturePhoto: 'Capturar Foto',
    },
    documentPreview: {
      frontPreview: 'Vista Previa Frontal',
      backPreview: 'Vista Previa Posterior',
      lookingGood: '¡Se Ve Bien!',
      checkDetails: 'Asegúrate de que todos los detalles sean claramente visibles',
      imageQuality: 'Calidad de imagen',
      qualityGood: 'Buena',
      usePhoto: 'Usar Esta Foto',
      retakePhoto: 'Volver a Tomar Foto',
    },
    documentCapture: {
      captureFront: 'Capturar Frente',
      captureBack: 'Capturar Reverso',
      alignDocument: 'Alinea el documento dentro del marco',
      captureButton: 'Capturar',
      retakeButton: 'Volver a tomar',
      confirmButton: 'Confirmar',
      frontSide: 'Lado Frontal',
      backSide: 'Lado Posterior',
      positionDocument: 'Posiciona tu documento en el marco',
      alignWithFrame: 'Alinea el documento con el marco',
      positionFrontId: 'Posicionar frente del ID',
      positionBackId: 'Posicionar reverso del ID',
      scanFrontId: 'Escanear Frente del ID',
      scanBackId: 'Escanear Reverso del ID',
      positionWithinFrame: 'Posiciona el lado frontal dentro del marco',
      flipAndScan: 'Voltea tu ID y escanea el reverso',
      goodLight: 'Buena luz',
      flatSurface: 'Superficie plana',
      frontCaptured: 'Frente capturado',
      captureIdCard: 'Capturar Documento de Identidad',
      capturePassport: 'Capturar Pasaporte',
      captureDriversLicense: 'Capturar Licencia de Conducir',
    },
    processing: {
      title: 'Procesando',
      description: 'Por favor espera mientras procesamos',
      analyzingDocument: 'Analizando documento...',
      almostDone: 'Casi terminamos...',
      faceVerified: 'Rostro verificado',
      livenessConfirmed: 'Vivacidad confirmada',
      processingDocuments: 'Procesando documentos...',
    },
    success: {
      title: 'Verificación de Edad Aprobada',
      description: 'Verificación de edad y vida completada',
      redirecting: 'Redirigiendo...',
      summary: 'Resumen',
      faceMatch: 'Coincidencia Facial',
      liveness: 'Vivacidad',
      age: 'Edad',
      idDocument: 'Documento de ID',
      pass: 'Aprobado',
      fail: 'Fallido',
      valid: 'Válido',
      notApplicable: 'N/A',
      done: 'Listo',
      feedbackTitle: 'Por favor, califique su experiencia',
      feedbackPlaceholder: 'Díganos cómo podemos mejorar...',
      skip: 'Omitir',
      submit: 'Enviar',
    },
    failure: {
      title: 'Control de Edad Fallido',
      description: 'No pudimos completar el control. Por favor, intenta de nuevo.',
      tryAgainButton: 'Intentar de Nuevo',
      leaveButton: 'Salir',
      attemptsLeft: '{{count}} intento(s) restante(s)',
      noAttemptsLeft: 'No quedan más intentos',
    },
    error: {
      title: 'Configuración Requerida',
      description:
        'Faltan algunos parámetros requeridos en la URL. Por favor, contacta al administrador del sitio.',
      missingParams: 'Parámetros Faltantes',
      requiredParams: 'Parámetros Requeridos',
      technicalDetails: 'Detalles Técnicos',
      urlMustInclude: 'La URL debe incluir estos parámetros de consulta:',
    },
    underage: {
      title: 'Requisito de Edad No Cumplido',
      description: 'Debes tener al menos {{minAge}} años para acceder a este servicio.',
    },
    consent: {
      title: 'Ayuda a Mejorar el Control de Edad',
      description:
        'Permite que AppBouncer recopile una pequeña cantidad de datos de uso anónimos (como capturas de cámara) para mejorar la verificación de edad y las verificaciones de respaldo. Tus datos nunca se venden ni se proporcionan a terceros.',
      allowButton: 'Permitir',
    },
    documentConsent: {
      title: 'Documento Requerido',
      description:
        'Para completar el control, necesitamos verificar tu edad usando un documento oficial. ¿Consientes subir tu documento de identidad?',
      allowButton: 'Continuar con Documento',
      declineButton: 'Rechazar',
      privacyNote: 'Tu documento será procesado de forma segura y eliminado después del control.',
    },
    selfDeclaration: {
      title: 'Tu Fecha de Nacimiento',
      description: 'Por favor, ingresa tu fecha de nacimiento para continuar',
      day: 'Día',
      month: 'Mes',
      year: 'Año',
      yourAge: 'Tu edad:',
      years: 'años',
      confirmButton: 'Continuar',
      months: {
        january: 'Enero',
        february: 'Febrero',
        march: 'Marzo',
        april: 'Abril',
        may: 'Mayo',
        june: 'Junio',
        july: 'Julio',
        august: 'Agosto',
        september: 'Septiembre',
        october: 'Octubre',
        november: 'Noviembre',
        december: 'Diciembre',
      },
    },
  },

  lighting: {
    warning: 'Problema de Iluminación',
    adjust: 'Ajusta la iluminación',
    lowLight: 'Luz baja detectada',
    tooBright: 'Demasiado brillante',
    poorContrast: 'Contraste bajo',
    improveLighting: 'Busca mejor iluminación',
    moveToLighter: 'Ve a un área más iluminada',
    videoNotReady: 'Video no está listo',
  },

  steps: {
    faceDetection: 'Detección Facial',
    liveness: 'Verificación de Vida',
    age: 'Control de Edad',
    documentScan: 'Escaneo de Documento',
    selfDeclaration: 'Autodeclaración',
  },

  status: {
    cameraStarted: 'Cámara iniciada',
    cameraReady: 'Cámara lista',
    cameraAccessDenied: 'Acceso a cámara denegado',
    cameraError: 'Error de cámara',
    cameraNotAvailable: 'Cámara no disponible',
    noFaceDetected: 'No se detectó rostro',
    livenessVerified: 'Vivacidad verificada',
    livenessCheckFailed: 'Verificación de vida fallida',
    verifyingAge: 'Verificando edad...',
    processingDocument: 'Procesando documento...',
    documentProcessed: 'Documento procesado exitosamente',
    verificationCancelled: 'Control cancelado',
    improveLightingForAge: 'Mejora la iluminación para obtener resultados precisos',
    getReady: 'Prepárate',
    preparingLiveness: 'Preparando verificación de vida...',
    uploadingSelfie: 'Subiendo imágenes de selfie...',
    preparingUpload: 'Preparando subida...',
    uploadingFront: 'Subiendo documento frontal...',
    uploadingBack: 'Subiendo documento posterior...',
    processingDocuments: 'Procesando documentos...',
    documentSubmissionFailed: 'Error al enviar documento. Por favor, intenta de nuevo.',
    selectJpegOrPng: 'Por favor, selecciona una imagen JPEG o PNG',
    fileTooLarge: 'Archivo demasiado grande. Máximo 5MB permitido.',
    errorReadingFile: 'Error al leer el archivo',
    frontDocumentRequired: 'Se requiere imagen del documento frontal',
    verificationFailedClose: 'Control fallido. Serás redirigido de vuelta.',
    verificationCompleteClose: '¡Control completo! Serás redirigido de vuelta.',
    analyzingFace: 'Analizando rostro...',
  },
};
